import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Select from "react-select";
import axios from "axios";
import DatePickerModal from "../../components/DatePickerModal/DatePickerModal";
import Lf23Loader from "../../components/Lf23Loader";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001/api";
const BASE = `${API_URL}/report-sap-capisquadra`;
// La classificazione vuole login + permesso della pagina.
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });
const selectMenuPortalStyles = { menuPortal: (base) => ({ ...base, zIndex: 9999 }) };

// breve = badge in tabella; label = filtro ed Excel; title = tooltip del badge.
const MOTIVI = {
    non_censito: { breve: "Non censito", label: "Codice non censito in GECOS", title: "Codice non censito in GECOS: nessuna attività del catalogo ha questo codice come centro di costo" },
    senza_turno: { breve: "Senza turno", label: "Nessun turno GECOS", title: "Nessun turno GECOS: l'attività c'è, ma quel giorno non ha turni" },
    senza_codice: { breve: "Senza codice", label: "Senza codice itinerario", title: "Il SAP non riporta il codice itinerario: non si può ignorare" },
};

// Icone delle azioni (stile feather, come il resto della pagina).
const ICONE = {
    ignorato: <><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" /><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" /><line x1="1" y1="1" x2="23" y2="23" /></>,
    ripristina: <><polyline points="1 4 1 10 7 10" /><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" /></>,
};
const Icona = ({ nome }) => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        {ICONE[nome]}
    </svg>
);
// Unica azione: "Gestito" è implicito (task 536), un servizio sistemato su GECOS risulta
// abbinato ed esce da solo dalla lista.
const AZIONI = [
    { stato: "ignorato", label: "Ignora", title: "Da ignorare: non ricomparirà nei prossimi caricamenti" },
];
const STATI = {
    ignorato: { label: "Ignorato" },
};
const SENZA_TIPO = "Senza tipo prestazione";
// Durata dell'evidenziazione di una riga che cambia stato: deve coincidere con le animazioni
// report-sap-nab-uscita / report-sap-nab-flash nel CSS (come USCITA_MS nel tab Report).
const USCITA_MS = 1200;

// Filtro Stato: di default si vede il lavoro da fare (i non abbinati non ignorati).
// Gli ignorati si ritrovano da qui o cliccando la loro KPI card.
const STATO_OPTIONS = [
    { value: "da", label: "Non abbinati" },
    { value: "ignorato", label: "Ignorati" },
];
const STATO_DEFAULT = STATO_OPTIONS.slice(0, 1);
// Filtri del pannello: come nel tab Report si modificano in bozza e valgono al click su "Applica".
const FILTRI_DEFAULT = { stato: STATO_DEFAULT, motivo: [], tipo: [], cdl: [], cerca: "" };

const formatItalianFromIso = (iso) => {
    if (!iso) return "—";
    const [y, m, d] = String(iso).slice(0, 10).split("-");
    return `${d}/${m}/${y}`;
};
const ora = (t) => (t ? String(t).slice(0, 5) : "—");
const statoDi = (r) => r.classificazione?.stato || null;
const conteggio = (n, uno, molti) => `${n} ${n === 1 ? uno : molti}`;

// Tab "Non abbinati" (task 527): i servizi del file SAP che nessun turno GECOS del report
// richiama. "Ignora" vale per codice itinerario anche per i caricamenti successivi (il servizio
// non ricompare); gli altri restano in lista finché su GECOS non risultano abbinati.
const TabNonAbbinati = () => {
    const [date, setDate] = useState("");
    const [dati, setDati] = useState(null);           // { date, totaleSap, abbinati, nonAbbinati, righe, classificatiAltrove }
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [showFilters, setShowFilters] = useState(true);
    const [bozza, setBozza] = useState(FILTRI_DEFAULT);         // quello che si vede nel pannello
    const [applicati, setApplicati] = useState(FILTRI_DEFAULT); // quello che filtra la lista
    const setFiltro = (k, v) => setBozza((prev) => ({ ...prev, [k]: v }));
    const applicaFiltri = () => setApplicati(bozza);
    const resetFiltri = () => { setBozza(FILTRI_DEFAULT); setApplicati(FILTRI_DEFAULT); };

    const [aperte, setAperte] = useState({});          // tipo prestazione -> aperta (default: aperta)
    const [expanded, setExpanded] = useState(() => new Set()); // codici con il dettaglio aperto
    const [selezionati, setSelezionati] = useState(() => new Map()); // codice -> riga
    const [busy, setBusy] = useState(false);
    const [testoBusy, setTestoBusy] = useState("");     // didascalia del loader durante il salvataggio
    const [veloBusy, setVeloBusy] = useState(false);     // il velo compare solo se il salvataggio va per le lunghe

    // Un salvataggio dura di solito pochi ms: un velo che appare e sparisce subito sembra un
    // refresh della pagina. Lo si mostra solo dopo 300 ms.
    useEffect(() => {
        if (!busy) { setVeloBusy(false); return undefined; }
        const t = setTimeout(() => setVeloBusy(true), 300);
        return () => clearTimeout(t);
    }, [busy]);
    const [esito, setEsito] = useState(null);           // { tipo: "ok"|"warn", testo }
    const [exporting, setExporting] = useState(false);

    // Data di default: l'ultimo SAP caricato, come nel tab Report.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await axios.get(`${BASE}/default-date`);
                if (!cancelled && res.data?.date) setDate(res.data.date);
            } catch (err) {
                console.error("Errore default-date:", err);
                if (!cancelled) setError("Errore nel recupero della data di default.");
            }
        })();
        return () => { cancelled = true; };
    }, []);

    // `forza`: "Aggiorna" rifà il confronto con GECOS invece di usare quello in cache (5 minuti).
    const carica = useCallback(async (d, forza = false) => {
        if (!d) return;
        setLoading(true);
        setError(null);
        try {
            const res = await axios.get(`${BASE}/non-abbinati`, { params: { date: d, ...(forza ? { refresh: 1 } : {}) } });
            setDati(res.data);
            setSelezionati(new Map());
            setAperte({});
            setExpanded(new Set());
        } catch (err) {
            console.error("Errore non-abbinati:", err);
            setError(err.response?.data?.error || "Errore nel recupero dei servizi SAP non abbinati.");
            setDati(null);
        } finally {
            setLoading(false);
        }
    }, []);

    // Righe appena cambiate di stato: codice -> { stato, esce }. Si colorano nel nuovo stato;
    // quelle che il filtro non mostrerebbe più restano in vista e si dissolvono (esce=true).
    const [evidenziate, setEvidenziate] = useState(() => new Map());
    const timerEvid = useRef(new Map());
    useEffect(() => () => timerEvid.current.forEach(clearTimeout), []);
    const evidenzia = (voci) => {
        if (!voci.length) return;
        setEvidenziate((prev) => {
            const n = new Map(prev);
            voci.forEach((v) => n.set(v.codice, v));
            return n;
        });
        voci.forEach((v) => {
            clearTimeout(timerEvid.current.get(v.codice));
            timerEvid.current.set(v.codice, setTimeout(() => {
                timerEvid.current.delete(v.codice);
                setEvidenziate((prev) => {
                    if (!prev.has(v.codice)) return prev;
                    const n = new Map(prev);
                    n.delete(v.codice);
                    return n;
                });
            }, USCITA_MS));
        });
    };

    useEffect(() => {
        if (!date) return;
        setEsito(null);
        timerEvid.current.forEach(clearTimeout);
        timerEvid.current.clear();
        setEvidenziate(new Map());
        carica(date);
    }, [date, carica]);

    const righe = useMemo(() => dati?.righe || [], [dati]);

    const contatori = useMemo(() => {
        const c = { nonAbbinati: 0, ignorato: 0 };
        for (const r of righe) {
            const s = statoDi(r);
            if (s) c[s] += 1; else c.nonAbbinati += 1;
        }
        return c;
    }, [righe]);

    const opzioni = (valori) => [...new Set(valori.filter(Boolean))].sort((a, b) => a.localeCompare(b, "it")).map((v) => ({ value: v, label: v }));
    const tipoOptions = useMemo(() => opzioni(righe.map((r) => r.tipo_prestazione || SENZA_TIPO)), [righe]);
    const cdlOptions = useMemo(() => opzioni(righe.map((r) => r.cdl)), [righe]);
    const motivoOptions = useMemo(
        () => Object.entries(MOTIVI).filter(([k]) => righe.some((r) => r.motivo === k)).map(([k, v]) => ({ value: k, label: v.label })),
        [righe]
    );

    // Il filtro Stato di default lascia fuori gli ignorati: sono quelli che "non devono
    // ricomparire". Una riga che sta uscendo per un cambio di stato resta finché si dissolve.
    const filtrate = useMemo(() => {
        const motivi = new Set(applicati.motivo.map((o) => o.value));
        const tipi = new Set(applicati.tipo.map((o) => o.value));
        const cdl = new Set(applicati.cdl.map((o) => o.value));
        const stati = new Set(applicati.stato.map((o) => o.value));
        const q = applicati.cerca.trim().toLowerCase();
        return righe.filter((r) => {
            if (stati.size && !stati.has(statoDi(r) || "da") && !evidenziate.get(r.codice_itinerario)?.esce) return false;
            if (motivi.size && !motivi.has(r.motivo)) return false;
            if (tipi.size && !tipi.has(r.tipo_prestazione || SENZA_TIPO)) return false;
            if (cdl.size && !cdl.has(r.cdl)) return false;
            if (q && ![r.codice_itinerario, r.descrizione_itinerario, r.servizio, r.localita].some((v) => String(v || "").toLowerCase().includes(q))) return false;
            return true;
        });
    }, [righe, applicati, evidenziate]);

    // La selezione tiene solo le righe visibili: dopo un cambio di filtro (Applica, KPI card,
    // ricerca) un'azione sulla selezione non deve toccare righe che non si vedono più.
    useEffect(() => {
        setSelezionati((prev) => {
            if (!prev.size) return prev;
            const visibili = new Set(filtrate.map((r) => r.codice_itinerario));
            const next = new Map([...prev].filter(([codice]) => visibili.has(codice)));
            return next.size === prev.size ? prev : next;
        });
    }, [filtrate]);

    // Una card per tipo prestazione, le più numerose in alto.
    const gruppi = useMemo(() => {
        const m = new Map();
        for (const r of filtrate) {
            const k = r.tipo_prestazione || SENZA_TIPO;
            if (!m.has(k)) m.set(k, []);
            m.get(k).push(r);
        }
        return [...m.entries()]
            .map(([nome, rows]) => ({ nome, rows }))
            .sort((a, b) => b.rows.length - a.rows.length || a.nome.localeCompare(b.nome, "it"));
    }, [filtrate]);

    // Ignorati che non sono tra i non abbinati di questa data: stesso filtro Stato e ricerca.
    const altrove = useMemo(() => {
        const stati = new Set(applicati.stato.map((o) => o.value));
        const q = applicati.cerca.trim().toLowerCase();
        return (dati?.classificatiAltrove || []).filter((r) => {
            const esce = !!evidenziate.get(r.codice_itinerario)?.esce;
            // Ripristinata: fuori da questo SAP non ha più niente da mostrare, resta solo per dissolversi.
            if (!r.classificazione) return esce;
            if (stati.size && !stati.has(r.classificazione.stato) && !esce) return false;
            if (q && ![r.codice_itinerario, r.descrizione_itinerario].some((v) => String(v || "").toLowerCase().includes(q))) return false;
            return true;
        });
    }, [dati, applicati, evidenziate]);
    const altroviPerStato = useMemo(() => {
        const c = { ignorato: 0 };
        for (const r of dati?.classificatiAltrove || []) if (r.classificazione) c[r.classificazione.stato] += 1;
        return c;
    }, [dati]);

    // Click su una KPI card di stato: mostra solo quello stato; di nuovo click = torna al default.
    const soloStato = (valore) => {
        // Vale subito (niente "Applica"): anche il pannello si allinea.
        const attivo = applicati.stato.length === 1 && applicati.stato[0].value === valore;
        const stato = attivo ? STATO_DEFAULT : STATO_OPTIONS.filter((o) => o.value === valore);
        setBozza((prev) => ({ ...prev, stato }));
        setApplicati((prev) => ({ ...prev, stato }));
    };

    const isAperta = (g) => (aperte[g.nome] !== undefined ? aperte[g.nome] : true);
    const toggleGruppo = (g) => setAperte((prev) => ({ ...prev, [g.nome]: !isAperta(g) }));
    const toggleExpanded = (codice) => setExpanded((prev) => {
        const next = new Set(prev);
        if (next.has(codice)) next.delete(codice); else next.add(codice);
        return next;
    });

    const classificabili = (rows) => rows.filter((r) => r.codice_itinerario);
    const toggleSelezione = (r) => setSelezionati((prev) => {
        const next = new Map(prev);
        if (next.has(r.codice_itinerario)) next.delete(r.codice_itinerario); else next.set(r.codice_itinerario, r);
        return next;
    });
    const tuttiSelezionati = (rows) => classificabili(rows).length > 0 && classificabili(rows).every((r) => selezionati.has(r.codice_itinerario));
    const toggleTutti = (rows) => setSelezionati((prev) => {
        const next = new Map(prev);
        const lista = classificabili(rows);
        if (lista.every((r) => next.has(r.codice_itinerario))) lista.forEach((r) => next.delete(r.codice_itinerario));
        else lista.forEach((r) => next.set(r.codice_itinerario, r));
        return next;
    });

    // stato null = ripristina (torna tra i non abbinati).
    // Applica alla lista solo le righe che il backend dice di aver toccato: niente
    // ricaricamento completo, che rifarebbe il confronto con GECOS (~15 s).
    const applicaEsito = ({ classificazioni = [], ripristinati = [] }) => {
        const nuove = new Map(classificazioni.map((c) => [c.codice_itinerario, c.classificazione]));
        const via = new Set(ripristinati);
        const aggiorna = (r) => {
            if (nuove.has(r.codice_itinerario)) return { ...r, classificazione: nuove.get(r.codice_itinerario) };
            if (via.has(r.codice_itinerario)) return { ...r, classificazione: null };
            return r;
        };
        setDati((prev) => prev && ({
            ...prev,
            righe: prev.righe.map(aggiorna),
            // Una ripristinata fuori da questo SAP resta con classificazione null finché si
            // dissolve; il prossimo caricamento non la riporta più.
            classificatiAltrove: prev.classificatiAltrove.map(aggiorna),
        }));
    };

    const TESTI_BUSY = { ignorato: "Segno come ignorato..." };

    const classifica = async (servizi, stato) => {
        if (busy || !servizi.length || !dati) return;
        setBusy(true);
        setTestoBusy(stato === null ? "Ripristino..." : TESTI_BUSY[stato]);
        try {
            const res = await axios.post(`${BASE}/non-abbinati/classifica`, {
                date: dati.date,
                stato,
                servizi: servizi.map((r) => ({
                    codice_itinerario: r.codice_itinerario,
                    descrizione_itinerario: r.descrizione_itinerario,
                    tipo_prestazione: r.tipo_prestazione,
                })),
            }, { headers: authHeaders() });
            const n = res.data?.aggiornati ?? 0;
            const invariati = servizi.length - n;
            const cosa = conteggio(n, "servizio", "servizi");
            let testo;
            if (stato === null) testo = `${cosa} di nuovo tra i non abbinati.`;
            else testo = `${cosa} segnati come ignorati: non ricompariranno nei prossimi caricamenti SAP. Li ritrovi cliccando la card «Ignorati».`;
            if (stato !== null && invariati > 0) testo += ` ${conteggio(invariati, "era", "erano")} già in questo stato.`;
            setSelezionati(new Map());
            setEsito({ tipo: "ok", testo });
            // Un backend che non restituisce le righe salvate (es. non ancora aggiornato):
            // si ricarica la lista, altrimenti la pagina resterebbe ferma al vecchio stato.
            if (Array.isArray(res.data?.classificazioni)) {
                applicaEsito(res.data);
                const stati = new Set(applicati.stato.map((o) => o.value));
                const inAltrove = new Set((dati.classificatiAltrove || []).map((r) => r.codice_itinerario));
                const toccati = [
                    ...res.data.classificazioni.map((c) => ({ codice: c.codice_itinerario, stato: c.classificazione.stato })),
                    ...(res.data.ripristinati || []).map((codice) => ({ codice, stato: "da" })),
                ];
                evidenzia(toccati.map((v) => ({
                    ...v,
                    esce: (stati.size > 0 && !stati.has(v.stato)) || (v.stato === "da" && inAltrove.has(v.codice)),
                })));
            }
            else await carica(dati.date);
        } catch (err) {
            console.error("Errore classificazione:", err);
            setEsito({ tipo: "warn", testo: err.response?.data?.error || "Errore nel salvataggio della classificazione." });
        } finally {
            setBusy(false);
        }
    };

    const handleExportExcel = async () => {
        if (exporting || !dati) return;
        setExporting(true);
        try {
            const rows = filtrate.map((r) => ({
                servizio: r.servizio,
                codice_itinerario: r.codice_itinerario,
                descrizione_itinerario: r.descrizione_itinerario,
                tipo_prestazione: r.tipo_prestazione,
                cdl: r.cdl,
                localita: r.localita,
                ora_inizio_prevista: ora(r.ora_inizio_prevista),
                ora_fine_prevista: ora(r.ora_fine_prevista),
                mezzi_previsti: r.mezzi_previsti,
                autisti_previsti: r.autisti_previsti,
                stato_servizio: r.stato_servizio,
                motivo: MOTIVI[r.motivo]?.label || r.motivo,
                attivita: r.attivita.map((a) => a.nome).join(", "),
                tipologia: [...new Set(r.attivita.map((a) => a.tipologia).filter(Boolean))].join(", "),
                classificazione: STATI[statoDi(r)]?.label || "Non abbinato",
                classificato_il: r.classificazione ? formatItalianFromIso(r.classificazione.data_sap) : "",
                classificato_da: r.classificazione?.da || "",
            }));
            const res = await axios.post(`${BASE}/non-abbinati/export`, { date: dati.date, rows }, { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement("a");
            a.href = url;
            a.download = `sap_non_abbinati_${dati.date}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error("Errore export:", err);
            alert("Errore nell'esportazione Excel.");
        } finally {
            setExporting(false);
        }
    };

    const selezione = [...selezionati.values()];
    const selezioneClassificata = selezione.some((r) => statoDi(r));
    const percentuale = dati?.totaleSap ? Math.round((dati.abbinati / dati.totaleSap) * 100) : 0;

    // Contatori della data: i primi due dicono quanto il SAP combacia con GECOS, gli altri
    // quanti servizi restano da sistemare e quanti sono stati ignorati.
    const kpi = [
        { id: "sap", colore: "slate", label: "Servizi SAP", valore: dati?.totaleSap || 0,
            title: `Servizi nel file SAP di questa data${dati?.esclusiAbiti ? `, esclusi ${conteggio(dati.esclusiAbiti, "servizio", "servizi")} abiti usati` : ""}`,
            icona: <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /></> },
        { id: "abbinati", colore: "green", label: "Abbinati a GECOS", valore: dati?.abbinati || 0, extra: `${percentuale}%`, title: "Servizi SAP con almeno un turno GECOS nel report",
            icona: <><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></> },
        { id: "da", filtro: "da", colore: "blue", label: "Non abbinati", valore: contatori.nonAbbinati, title: "Servizi SAP senza riscontro in GECOS, non ignorati: escono da soli quando su GECOS risultano abbinati",
            icona: <><circle cx="12" cy="12" r="10" /><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" /><line x1="12" y1="17" x2="12.01" y2="17" /></> },
        { id: "ignorato", filtro: "ignorato", colore: "gray", label: "Ignorati", valore: contatori.ignorato, title: "Non ricompaiono nei prossimi caricamenti", icona: ICONE.ignorato },
    ];

    // Classe della riga appena cambiata: colore del nuovo stato; "uscita" se si dissolve.
    const classeEvid = (codice) => {
        const v = codice ? evidenziate.get(codice) : null;
        if (!v) return undefined;
        return `report-sap-nab-evid report-sap-nab-evid--${v.stato} ${v.esce ? "report-sap-nab-evid--uscita" : "report-sap-nab-evid--flash"}`;
    };

    // "dal 14/10/2026 · admin": tooltip del pulsante acceso.
    const infoClassificazione = (r) => (r.classificazione
        ? `dal ${formatItalianFromIso(r.classificazione.data_sap)}${r.classificazione.da ? ` · ${r.classificazione.da}` : ""}`
        : "");

    // Un solo gruppo di pulsanti, uguale per ogni riga e per la selezione: lo stato attuale è
    // il pulsante acceso (nessuno acceso = non abbinato), ↺ in fondo lo riporta tra i non abbinati.
    // ripristina: false | "icona" | "testo". soloAttuale: solo lo stato acceso (righe di altri
    // SAP: riclassificarle qui segnerebbe la data sbagliata).
    const gruppoAzioni = (servizi, { attuale = null, info = "", ripristina = false, soloAttuale = false } = {}) => (
        <div className="report-sap-nab-seg" role="group" aria-label="Classifica">
            {AZIONI.filter((a) => !soloAttuale || a.stato === attuale).map((a) => (
                <button key={a.stato} type="button" className={`report-sap-nab-seg-btn report-sap-nab-seg-btn--${a.stato}`}
                    aria-pressed={attuale === a.stato} disabled={busy || attuale === a.stato}
                    onClick={() => classifica(servizi, a.stato)} title={attuale === a.stato ? `Stato attuale${info ? `, ${info}` : ""}` : a.title}>
                    <Icona nome={a.stato} />{a.label}
                </button>
            ))}
            {ripristina && (
                <button type="button" className={`report-sap-nab-seg-btn report-sap-nab-seg-btn--ripristina${ripristina === "icona" ? " report-sap-nab-seg-btn--icona" : ""}`}
                    disabled={busy} onClick={() => classifica(servizi, null)} title="Ripristina: torna tra i non abbinati" aria-label="Ripristina">
                    <Icona nome="ripristina" />{ripristina === "testo" && "Ripristina"}
                </button>
            )}
        </div>
    );

    const azioniRiga = (r, { soloAttuale = false } = {}) => {
        const s = statoDi(r);
        if (!r.codice_itinerario || (soloAttuale && !s)) return <span className="report-sap-nab-muted">—</span>;
        return gruppoAzioni([r], { attuale: s, info: infoClassificazione(r), ripristina: s ? "icona" : false, soloAttuale });
    };

    // Un unico contenitore che scorre: la lista non ha una barra sua e si allunga quanto serve.
    return (
        <div className="report-sap-nab">
            <div className="report-sap-toolbar">
                <button type="button" className={`toggle-filters-btn ${showFilters ? "active" : ""}`} onClick={() => setShowFilters((v) => !v)}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
                    </svg>
                    {showFilters ? "Nascondi Filtri" : "Mostra Filtri"}
                </button>
            </div>

            {showFilters && (
                <div className="filter-panel">
                    <div className="filter-grid">
                        <div className="filter-group">
                            <label>Data SAP</label>
                            <DatePickerModal value={date} onChange={(e) => setDate(e.target.value)} label="Seleziona data" />
                        </div>
                        <div className="filter-group">
                            <label>Stato</label>
                            <Select isMulti placeholder="Tutti gli stati" options={STATO_OPTIONS} value={bozza.stato}
                                onChange={(v) => setFiltro("stato", v || [])} classNamePrefix="filter-select" isSearchable={false} closeMenuOnSelect={false}
                                menuPortalTarget={typeof document !== "undefined" ? document.body : null} styles={selectMenuPortalStyles} />
                        </div>
                        <div className="filter-group">
                            <label>Motivo</label>
                            <Select isMulti placeholder="Tutti i motivi" options={motivoOptions} value={bozza.motivo}
                                onChange={(v) => setFiltro("motivo", v || [])} classNamePrefix="filter-select" isSearchable={false}
                                menuPortalTarget={typeof document !== "undefined" ? document.body : null} styles={selectMenuPortalStyles} />
                        </div>
                        <div className="filter-group">
                            <label>Tipo prestazione</label>
                            <Select isMulti placeholder="Tutti i tipi" options={tipoOptions} value={bozza.tipo}
                                onChange={(v) => setFiltro("tipo", v || [])} classNamePrefix="filter-select" closeMenuOnSelect={false}
                                menuPortalTarget={typeof document !== "undefined" ? document.body : null} styles={selectMenuPortalStyles} />
                        </div>
                        <div className="filter-group">
                            <label>CDL</label>
                            <Select isMulti placeholder="Tutti i CDL" options={cdlOptions} value={bozza.cdl}
                                onChange={(v) => setFiltro("cdl", v || [])} classNamePrefix="filter-select" closeMenuOnSelect={false}
                                menuPortalTarget={typeof document !== "undefined" ? document.body : null} styles={selectMenuPortalStyles} />
                        </div>
                        <div className="filter-group">
                            <label>Cerca</label>
                            <input type="search" className="report-sap-nab-search" value={bozza.cerca} onChange={(e) => setFiltro("cerca", e.target.value)}
                                onKeyDown={(e) => { if (e.key === "Enter") applicaFiltri(); }} placeholder="Codice, descrizione, servizio..." />
                        </div>
                    </div>
                    <div className="report-sap-filter-actions">
                        <button type="button" className="report-sap-btn-secondary" onClick={resetFiltri}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                                <path d="M3 3v5h5" />
                            </svg>
                            Reset
                        </button>
                        <button type="button" className="report-sap-btn-primary" onClick={applicaFiltri}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="20 6 9 17 4 12" />
                            </svg>
                            Applica
                        </button>
                    </div>
                </div>
            )}

            {dati && dati.totaleSap > 0 && (
                <div className="report-sap-nab-kpi-cards">
                    {kpi.map((k) => {
                        const attiva = k.filtro && applicati.stato.length === 1 && applicati.stato[0].value === k.filtro;
                        const altrove = k.filtro && k.filtro !== "da" ? altroviPerStato[k.filtro] : 0;
                        // Le card di stato sono pulsanti: filtrano la lista su quello stato.
                        const Tag = k.filtro ? "button" : "div";
                        return (
                        <Tag key={k.id} {...(k.filtro ? { type: "button", onClick: () => soloStato(k.filtro), "aria-pressed": attiva } : {})}
                            className={`report-sap-nab-kpi-card report-sap-nab-kpi--${k.colore}${k.filtro ? " report-sap-nab-kpi-card--filtro" : ""}${attiva ? " attiva" : ""}`}
                            title={k.filtro ? `${k.title}. ${attiva ? "Clicca per tornare alla vista normale" : "Clicca per vedere solo questi"}` : k.title}>
                            <div className="report-sap-nab-kpi-icon">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{k.icona}</svg>
                            </div>
                            <div className="report-sap-nab-kpi-content">
                                <span className="report-sap-nab-kpi-value">
                                    {/* Numero semplice: AnimatedCounter riparte da 0 a ogni cambio, e qui i
                                        contatori cambiano a ogni classificazione (sembrava un refresh). */}
                                    {k.valore.toLocaleString("it-IT")}
                                    {k.extra && <span className="report-sap-nab-kpi-extra">{k.extra}</span>}
                                </span>
                                <span className="report-sap-nab-kpi-label">
                                    {k.label}{altrove > 0 && <span className="report-sap-nab-kpi-altrove"> · +{altrove} da altri SAP</span>}
                                </span>
                            </div>
                        </Tag>
                        );
                    })}
                </div>
            )}

            {error && <div className="report-sap-error" role="alert">{error}</div>}

            {esito && (
                <div className={`report-sap-na-esito report-sap-na-esito--${esito.tipo}`} role="status">
                    <span>{esito.testo}</span>
                    <button type="button" onClick={() => setEsito(null)} aria-label="Chiudi">×</button>
                </div>
            )}

            {selezionati.size > 0 && (
                // Sticky: scorrendo la lista resta agganciata in cima al tab.
                <div className="report-sap-na-selbar report-sap-nab-selbar">
                    <span>{conteggio(selezionati.size, "servizio selezionato", "servizi selezionati")}</span>
                    <div className="report-sap-na-selbar-actions report-sap-nab-selbar-actions">
                        {gruppoAzioni(selezione, { ripristina: selezioneClassificata ? "testo" : false })}
                        <button type="button" className="report-sap-nab-link" onClick={() => setSelezionati(new Map())} disabled={busy}>Deseleziona</button>
                    </div>
                </div>
            )}

            {/* Esporta e Aggiorna subito sopra la lista (anche dopo un errore, per riprovare) */}
            {date && (dati || error) && (
                <div className="report-sap-nab-list-actions">
                    {dati?.confrontoGecosAl && (
                        <span className="report-sap-nab-confronto" title="I turni GECOS vengono riletti ogni 5 minuti; Aggiorna li rilegge subito">
                            Confronto con GECOS delle {new Date(dati.confrontoGecosAl).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}
                        </span>
                    )}
                    <button type="button" className="report-sap-export-btn report-sap-nab-export-btn" onClick={handleExportExcel} disabled={exporting || filtrate.length === 0}
                        title="Esporta in Excel i servizi attualmente visibili">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
                        </svg>
                        {exporting ? "Esportazione…" : "Esporta Excel"}
                    </button>
                    <button type="button" className="report-sap-btn-primary" onClick={() => carica(date, true)} disabled={loading || !date}
                        title="Rilegge subito i turni GECOS">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8" /><path d="M21 3v5h-5" />
                        </svg>
                        {loading ? "Carico..." : "Aggiorna"}
                    </button>
                </div>
            )}

            {loading && !dati && <Lf23Loader testo="Confronto del SAP con i turni GECOS..." />}

            {!loading && dati && dati.totaleSap === 0 && !error && (
                <div className="report-sap-empty">Nessun file SAP caricato per il {formatItalianFromIso(dati.date)}.</div>
            )}
            {!loading && dati && dati.totaleSap > 0 && filtrate.length === 0 && altrove.length === 0 && !error && (
                <div className="report-sap-empty">
                    {righe.length === 0
                        ? `Tutti i servizi SAP del ${formatItalianFromIso(dati.date)} hanno un riscontro in GECOS.`
                        : "Nessun servizio da mostrare con questi filtri."}
                </div>
            )}

            {gruppi.length > 0 && (
            <div className="report-sap-list">
                {gruppi.map((g) => {
                    const aperta = isAperta(g);
                    return (
                        <section key={g.nome} className="report-sap-localita-card">
                            <div className="report-sap-localita-header">
                                <button type="button" className="report-sap-localita-header-toggle" onClick={() => toggleGruppo(g)} aria-expanded={aperta}>
                                    <div className="report-sap-localita-title">
                                        <svg className={`report-sap-chevron ${aperta ? "" : "collapsed"}`} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <polyline points="6 9 12 15 18 9" />
                                        </svg>
                                        <div className="report-sap-localita-heading">
                                            <span className="report-sap-localita-name">{g.nome}</span>
                                        </div>
                                    </div>
                                    <div className="report-sap-localita-kpi">
                                        <span className="report-sap-localita-count">{conteggio(g.rows.length, "servizio", "servizi")}</span>
                                    </div>
                                </button>
                            </div>

                            {aperta && (
                                <div className="report-sap-table-wrap">
                                    <table className="report-sap-table">
                                        <thead>
                                            <tr>
                                                <th style={{ width: "3%" }} className="report-sap-expand-col" aria-label="Dettagli" />
                                                <th style={{ width: "3%" }} className="report-sap-na-check-col">
                                                    <input type="checkbox" checked={tuttiSelezionati(g.rows)} onChange={() => toggleTutti(g.rows)} aria-label={`Seleziona tutti i servizi di ${g.nome}`} />
                                                </th>
                                                <th style={{ width: "9%" }}>Codice</th>
                                                <th style={{ width: "24%" }}>Descrizione itinerario</th>
                                                <th style={{ width: "8%" }}>Servizio</th>
                                                <th style={{ width: "7%" }}>CDL</th>
                                                <th style={{ width: "9%" }}>Orario</th>
                                                <th style={{ width: "10%" }}>Motivo</th>
                                                <th style={{ width: "27%" }} className="report-sap-action-col">Stato</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {g.rows.map((r) => {
                                                const key = r.codice_itinerario || `servizio-${r.servizio}`;
                                                const isExpanded = expanded.has(key);
                                                const motivo = MOTIVI[r.motivo];
                                                return (
                                                    <React.Fragment key={key}>
                                                        <tr className={classeEvid(r.codice_itinerario)}>
                                                            <td className="report-sap-expand-col">
                                                                <button type="button" className={`report-sap-expand-btn ${isExpanded ? "open" : ""}`} onClick={() => toggleExpanded(key)} title={isExpanded ? "Comprimi dettagli" : "Mostra dettagli"} aria-expanded={isExpanded}>
                                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6" /></svg>
                                                                </button>
                                                            </td>
                                                            <td className="report-sap-na-check-col">
                                                                <input type="checkbox" checked={selezionati.has(r.codice_itinerario)} onChange={() => toggleSelezione(r)}
                                                                    disabled={!r.codice_itinerario} aria-label="Seleziona servizio" />
                                                            </td>
                                                            <td className="report-sap-nab-mono">{r.codice_itinerario || "—"}</td>
                                                            <td className="report-sap-servizio-cell">{r.descrizione_itinerario || "—"}</td>
                                                            <td className="report-sap-nab-mono">{r.servizio || "—"}</td>
                                                            <td>{r.cdl || "—"}</td>
                                                            <td>{ora(r.ora_inizio_prevista)} – {ora(r.ora_fine_prevista)}</td>
                                                            <td>
                                                                <span className={`report-sap-nab-motivo report-sap-nab-motivo--${r.motivo}`} title={motivo?.title}>{motivo?.breve || r.motivo}</span>
                                                            </td>
                                                            <td className="report-sap-action-col">{azioniRiga(r)}</td>
                                                        </tr>
                                                        {isExpanded && (
                                                            <tr className={`report-sap-detail-row ${classeEvid(r.codice_itinerario)}`.trim()}>
                                                                <td colSpan={9}>
                                                                    <div className="report-sap-detail-panel">
                                                                        <div className="report-sap-detail-panel-accent" aria-hidden="true" />
                                                                        <div className="report-sap-detail-grid">
                                                                            <div className="report-sap-detail-item"><span className="report-sap-detail-label">Località</span><span className="report-sap-detail-value">{r.localita || "—"}</span></div>
                                                                            <div className="report-sap-detail-item"><span className="report-sap-detail-label">Mezzi / autisti previsti</span><span className="report-sap-detail-value">{r.mezzi_previsti || "—"} / {r.autisti_previsti || "—"}</span></div>
                                                                            <div className="report-sap-detail-item"><span className="report-sap-detail-label">Stato servizio SAP</span><span className="report-sap-detail-value report-sap-detail-value--mono">{r.stato_servizio || "—"}</span></div>
                                                                            {r.attivita.length > 0 && (
                                                                                <div className="report-sap-detail-item"><span className="report-sap-detail-label">Attività GECOS senza turno</span><span className="report-sap-detail-value">
                                                                                    {r.attivita.map((a) => `${a.nome}${a.tipologia ? ` (${a.tipologia})` : ""}`).join(" · ")}
                                                                                </span></div>
                                                                            )}
                                                                            {r.classificazione && (
                                                                                <div className="report-sap-detail-item"><span className="report-sap-detail-label">Ignorato</span><span className="report-sap-detail-value">
                                                                                    Sul SAP del {formatItalianFromIso(r.classificazione.data_sap)}{r.classificazione.da ? ` da ${r.classificazione.da}` : ""}
                                                                                </span></div>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </React.Fragment>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </section>
                    );
                })}
            </div>
            )}

            {altrove.length > 0 && (
                <div className="report-sap-list">
                    <section className="report-sap-localita-card">
                        <div className="report-sap-localita-header report-sap-nab-altrove-header">
                            <div className="report-sap-localita-heading">
                                <span className="report-sap-localita-name">Ignorati che non sono in questo SAP</span>
                                <span className="report-sap-localita-tipologie">
                                    Codici ignorati su altri caricamenti: non sono in questo file, oppure ora hanno un riscontro in GECOS.
                                </span>
                            </div>
                        </div>
                        <div className="report-sap-table-wrap">
                            <table className="report-sap-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: "14%" }}>Codice</th>
                                        <th style={{ width: "38%" }}>Descrizione itinerario</th>
                                        <th style={{ width: "26%" }}>Tipo prestazione</th>
                                        <th style={{ width: "22%" }} className="report-sap-action-col">Stato</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {altrove.map((r) => (
                                        <tr key={r.codice_itinerario} className={classeEvid(r.codice_itinerario)}>
                                            <td className="report-sap-nab-mono">{r.codice_itinerario}</td>
                                            <td>
                                                {r.descrizione_itinerario || "—"}
                                                {r.abbinato && <span className="report-sap-nab-muted"> · ora abbinato</span>}
                                            </td>
                                            <td>{r.tipo_prestazione || "—"}</td>
                                            <td className="report-sap-action-col">{azioniRiga(r, { soloAttuale: true })}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </section>
                </div>
            )}
            {/* Salvataggio di una classificazione o "Aggiorna" con la lista già a video: velo chiaro
                sulla pagina con il loader sopra, come anomalie-gps-loading-overlay e kg-teorici-table-overlay. */}
            {(veloBusy || (loading && dati)) && (
                <div className="report-sap-nab-overlay" role="status" aria-live="polite">
                    <Lf23Loader testo={loading ? "Confronto del SAP con i turni GECOS..." : testoBusy} />
                </div>
            )}
        </div>
    );
};

export default TabNonAbbinati;
